import React from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CheckCircle2 } from 'lucide-react';

interface ApprovalDonutChartProps {
  draft: number;
  pending: number;
  approved: number;
  rejected: number;
  className?: string;
}

const STATUS_COLORS = {
  draft: '#94a3b8',     // slate-400
  pending: '#f59e0b',   // amber-500
  approved: '#22c55e',  // green-500
  rejected: '#ef4444',  // red-500
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const ApprovalDonutChart: React.FC<ApprovalDonutChartProps> = ({
  draft,
  pending,
  approved,
  rejected,
  className,
}) => {
  const { t } = useTranslation();

  const total = draft + pending + approved + rejected;

  const data = [
    { name: t('approvals.status.draft', STATUS_LABELS.draft), value: draft, color: STATUS_COLORS.draft },
    { name: t('approvals.status.in_approval', STATUS_LABELS.pending), value: pending, color: STATUS_COLORS.pending },
    { name: t('approvals.status.approved', STATUS_LABELS.approved), value: approved, color: STATUS_COLORS.approved },
    { name: t('approvals.status.rejected', STATUS_LABELS.rejected), value: rejected, color: STATUS_COLORS.rejected },
  ].filter((d) => d.value > 0);

  if (total === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-4 w-4 text-orange-500" />
            {t('dashboard.approvalStatus', 'Approval Status')}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center text-muted-foreground">
          {t('dashboard.noApprovalData', 'No entries to display')}
        </CardContent>
      </Card>
    );
  }

  const approvedPercent = total > 0 ? Math.round((approved / total) * 100) : 0;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="h-4 w-4 text-orange-500" />
          {t('dashboard.approvalStatus', 'Approval Status')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center">
          <div className="flex-1 relative">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    `${value} (${Math.round((Number(value) / total) * 100)}%)`,
                    name,
                  ]}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--background))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="text-center">
                <p className="text-2xl font-bold">{approvedPercent}%</p>
                <p className="text-xs text-muted-foreground">
                  {t('dashboard.approved', 'Approved')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 ml-4">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-sm">
                  {entry.name}: <span className="font-medium">{entry.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ApprovalDonutChart;
